// filepath: src/pages/attendance/DownloadAttendancePage.jsx
import React, { useState, useEffect, useMemo, Fragment } from 'react';
import { toast } from 'react-toastify';
import API from '../../services/API';

const DownloadAttendancePage = () => {
  const [dateStr, setDateStr] = useState('');
  const [monthStr, setMonthStr] = useState('');
  const [filterType, setFilterType] = useState('day');
  const [isPortal] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [lastFilename, setLastFilename] = useState('');

  const [classes, setClasses] = useState([]);
  const [uniqueForms, setUniqueForms] = useState([]);
  const [incompleteClasses, setIncompleteClasses] = useState([]);
  const [selectedForm, setSelectedForm] = useState('');
  const [selectedClassId, setSelectedClassId] = useState('');
  const [startTime, setStartTime] = useState('');

  const [amalanScope, setAmalanScope] = useState('all');
  const [amalanMonth, setAmalanMonth] = useState('');
  const [amalanLimit, setAmalanLimit] = useState(50);
  const [rankingYear, setRankingYear] = useState(
    new Date().getFullYear().toString()
  );

  const [customTypeId, setCustomTypeId] = useState('');
  const [customTypes, setCustomTypes] = useState([]);
  const [isCustomDownloading, setIsCustomDownloading] = useState(false);

  const buildHeaders = () => ({
    'Content-Type': 'application/json',
    Accept: 'text/csv,application/json',
    token: localStorage.getItem('token') || '',
  });

  const getFilenameFromDisposition = (disposition, fallback) => {
    if (!disposition) return fallback;

    const starMatch = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(disposition);
    if (starMatch?.[1]) {
      try {
        return decodeURIComponent(starMatch[1]);
      } catch {
        // keep fallback parsing
      }
    }

    const plainMatch =
      /filename\s*=\s*"([^"]+)"/i.exec(disposition) ||
      /filename\s*=\s*([^;]+)/i.exec(disposition);

    return plainMatch?.[1]?.trim() || fallback;
  };

  const fetchClasses = () => {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      token: localStorage.getItem('token'),
    };

    API.get('/portal/getClasses', { headers })
      .then((response) => {
        if (response.data.response_code === 1000) {
          const fetched = response.data.data || [];
          setClasses(fetched);

          const formsFromAPI = Array.from(
            new Set(
              fetched
                .map((c) =>
                  c.tingkatan_moeis?.toString().toUpperCase().trim()
                )
                .filter(Boolean)
            )
          );

          setUniqueForms(formsFromAPI);

          const missing = fetched.filter(
            (c) => !c.tingkatan_moeis || !c.class_moeis_format
          );
          setIncompleteClasses(missing);
        } else {
          toast.error(response.data.message || 'Gagal mendapatkan senarai unit', {
            autoClose: 5000,
          });
        }
      })
      .catch(() => {
        toast.error('Ralat semasa mendapatkan senarai unit', {
          autoClose: 5000,
        });
      });
  };

  const fetchCustomAttendanceTypes = () => {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      token: localStorage.getItem('token'),
    };

    API.get('/customAttendance/type?is_portal=true', { headers })
      .then((response) => {
        if (response.data.response_code === 1000) {
          setCustomTypes(response.data.data || []);
        } else {
          toast.error(
            response.data.message || 'Gagal mendapatkan jenis kehadiran khas'
          );
        }
      })
      .catch(() => {
        toast.error('Ralat semasa mendapatkan jenis kehadiran khas');
      });
  };

  useEffect(() => {
    fetchClasses();
    fetchCustomAttendanceTypes();
  }, []);

  const filteredClasses = useMemo(() => {
    if (!selectedForm) return classes;

    return classes.filter(
      (c) =>
        (c.tingkatan_moeis?.toString().toUpperCase().trim() || '') ===
        selectedForm
    );
  }, [classes, selectedForm]);

  useEffect(() => {
    setSelectedClassId('');
  }, [selectedForm]);

  useEffect(() => {
    if (filterType === 'day') setMonthStr('');
    if (filterType === 'month') setDateStr('');
  }, [filterType]);

  const displayClassLabel = (c) => {
    return (
      c.class_moeis_format?.trim() ||
      c.class_name?.trim() ||
      c.class_code?.trim() ||
      `Unit ${c.class_id}`
    );
  };

  const handleDownload = async () => {
    if (filterType === 'day' && !dateStr) {
      toast.error('Sila pilih tarikh.');
      return;
    }

    if (filterType === 'month' && !monthStr) {
      toast.error('Sila pilih bulan.');
      return;
    }

    const baseDate = filterType === 'day' ? dateStr : `${monthStr}-01`;

    setIsDownloading(true);

    const url = `/attendance/downloadAttendance${
      isPortal ? '?is_portal=true' : ''
    }`;

    const payload = {
      filter_type: filterType,
      date: baseDate,
      ...(selectedClassId ? { class_id: selectedClassId } : {}),
      ...(selectedForm ? { form: selectedForm.toUpperCase().trim() } : {}),
      ...(startTime ? { start_time: startTime } : {}),
    };

    try {
      const res = await API.post(url, payload, {
        headers: buildHeaders(),
        responseType: 'blob',
        transformResponse: [(data) => data],
      });

      const contentType = (res.headers?.['content-type'] || '').toLowerCase();

      if (contentType.includes('application/json')) {
        const text = await res.data.text();

        try {
          const obj = JSON.parse(text);
          toast.error(obj?.message || 'Muat turun gagal.');
        } catch {
          toast.error('Muat turun gagal.');
        }

        return;
      }

      const suffix = filterType === 'day' ? dateStr : monthStr;
      let fallbackName = `kehadiran_terperinci_${filterType}_${suffix}`;

      if (selectedClassId) fallbackName += `_unit_${selectedClassId}`;
      if (startTime) fallbackName += `_dari_${startTime.replace(':', '')}`;

      fallbackName += `.csv`;

      const filename = getFilenameFromDisposition(
        res.headers?.['content-disposition'],
        fallbackName
      );

      const blob = new Blob([res.data], {
        type: contentType || 'text/csv;charset=utf-8',
      });

      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(blobUrl);

      setLastFilename(filename);
      toast.success('Fail CSV berjaya dimuat turun.');
    } catch (err) {
      const blob = err?.response?.data;
      const contentType = (
        err?.response?.headers?.['content-type'] || ''
      ).toLowerCase();

      if (blob && contentType.includes('application/json') && blob.text) {
        const text = await blob.text();

        try {
          const obj = JSON.parse(text);
          toast.error(obj?.message || 'Muat turun gagal.');
        } catch {
          toast.error('Muat turun gagal.');
        }
      } else {
        toast.error('Muat turun gagal.');
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSummaryDownload = async () => {
    if (!monthStr) {
      toast.error('Sila pilih bulan.');
      return;
    }

    const baseDate = `${monthStr}-01`;
    setIsDownloading(true);

    const url = `/attendance/attendanceSummaryView${
      isPortal ? '?is_portal=true' : ''
    }`;

    const payload = {
      filter_type: 'month',
      date: baseDate,
      ...(selectedClassId ? { class_id: selectedClassId } : {}),
      ...(selectedForm ? { form: selectedForm.toUpperCase().trim() } : {}),
    };

    try {
      const res = await API.post(url, payload, {
        headers: buildHeaders(),
        responseType: 'blob',
        transformResponse: [(data) => data],
      });

      const contentType = (res.headers?.['content-type'] || '').toLowerCase();

      if (contentType.includes('application/json')) {
        const text = await res.data.text();
        const obj = JSON.parse(text);
        toast.error(obj?.message || 'Muat turun gagal.');
        return;
      }

      const filename = getFilenameFromDisposition(
        res.headers?.['content-disposition'],
        `ringkasan_kehadiran_${monthStr}.csv`
      );

      const blob = new Blob([res.data], {
        type: 'text/csv;charset=utf-8',
      });

      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(blobUrl);

      setLastFilename(filename);
      toast.success('Ringkasan kehadiran berjaya dimuat turun.');
    } catch (err) {
      console.error(err);
      toast.error('Muat turun gagal.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCustomAttendanceSummaryDownload = async () => {
    if (!monthStr) {
      toast.error('Sila pilih bulan.');
      return;
    }

    const baseDate = `${monthStr}-01`;
    setIsCustomDownloading(true);

    const url = `/customAttendance/attendanceSummaryView${
      isPortal ? '?is_portal=true' : ''
    }`;

    const payload = {
      filter_type: 'month',
      date: baseDate,
      include_school_days_only: false,
      ...(selectedClassId ? { class_id: selectedClassId } : {}),
      ...(selectedForm ? { form: selectedForm.toUpperCase().trim() } : {}),
      ...(customTypeId ? { type_id: customTypeId } : {}),
    };

    try {
      const res = await API.post(url, payload, {
        headers: buildHeaders(),
        responseType: 'blob',
        transformResponse: [(data) => data],
      });

      const contentType = (res.headers?.['content-type'] || '').toLowerCase();

      if (contentType.includes('application/json')) {
        const text = await res.data.text();

        try {
          const obj = JSON.parse(text);
          toast.error(obj?.message || 'Muat turun gagal.');
        } catch {
          toast.error('Muat turun gagal.');
        }

        return;
      }

      const fallbackName = customTypeId
        ? `ringkasan_kehadiran_khas_${monthStr}_jenis_${customTypeId}.csv`
        : `ringkasan_kehadiran_khas_${monthStr}.csv`;

      const filename = getFilenameFromDisposition(
        res.headers?.['content-disposition'],
        fallbackName
      );

      const blob = new Blob([res.data], {
        type: contentType || 'text/csv;charset=utf-8',
      });

      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(blobUrl);

      setLastFilename(filename);
      toast.success('Ringkasan kehadiran khas berjaya dimuat turun.');
    } catch (err) {
      console.error(err);

      const blob = err?.response?.data;
      const contentType = (
        err?.response?.headers?.['content-type'] || ''
      ).toLowerCase();

      if (blob && contentType.includes('application/json') && blob.text) {
        const text = await blob.text();

        try {
          const obj = JSON.parse(text);
          toast.error(obj?.message || 'Muat turun gagal.');
        } catch {
          toast.error('Muat turun gagal.');
        }
      } else {
        toast.error('Muat turun gagal.');
      }
    } finally {
      setIsCustomDownloading(false);
    }
  };

  const handleAmalanBaikDownload = async () => {
    const scope = amalanScope;
    const month = amalanMonth;
    const limit = amalanLimit || 50;

    if (scope === 'month' && !month) {
      toast.error('Sila pilih bulan.');
      return;
    }

    const params = new URLSearchParams({
      scope,
      limit_top: limit,
      is_portal: true,
    });

    if (scope === 'month') params.append('month', month);

    const url = `/goodDeeds/downloadTopStudents?${params.toString()}`;

    const headers = {
      token: localStorage.getItem('token'),
      Accept: 'text/csv,application/json',
    };

    try {
      const res = await API.get(url, {
        headers,
        responseType: 'blob',
        transformResponse: [(data) => data],
      });

      const contentType = (res.headers?.['content-type'] || '').toLowerCase();

      if (contentType.includes('application/json')) {
        const text = await res.data.text();
        const obj = JSON.parse(text);
        toast.error(obj?.message || 'Muat turun gagal.');
        return;
      }

      const filename =
        getFilenameFromDisposition(
          res.headers?.['content-disposition'],
          `rekod_prestasi_${scope}_${month || 'semua'}.csv`
        );

      const blob = new Blob([res.data], {
        type: 'text/csv;charset=utf-8',
      });

      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(blobUrl);

      toast.success('Fail CSV prestasi berjaya dimuat turun.');
    } catch (err) {
      console.error(err);

      const blob = err?.response?.data;

      if (blob?.text) {
        const text = await blob.text();

        try {
          const obj = JSON.parse(text);
          toast.error(obj?.message || 'Muat turun gagal.');
        } catch {
          toast.error('Muat turun gagal.');
        }
      } else {
        toast.error('Muat turun gagal.');
      }
    }
  };

  const handleYearlyRankingDownload = async () => {
    if (!rankingYear) {
      toast.error('Sila pilih tahun.');
      return;
    }

    setIsDownloading(true);

    const url = `/attendance/attendanceDownloadBySchoolView${
      isPortal ? '?is_portal=true' : ''
    }`;

    const payload = {
      filter_type: 'year',
      date: `${rankingYear}-01-01`,
      ranking: true,
    };

    try {
      const res = await API.post(url, payload, {
        headers: buildHeaders(),
        responseType: 'blob',
        transformResponse: [(data) => data],
      });

      const contentType = (res.headers?.['content-type'] || '').toLowerCase();

      if (contentType.includes('application/json')) {
        const text = await res.data.text();
        const obj = JSON.parse(text);
        toast.error(obj?.message || 'Muat turun gagal.');
        return;
      }

      const filename = getFilenameFromDisposition(
        res.headers?.['content-disposition'],
        `ranking_kehadiran_${rankingYear}.csv`
      );

      const blob = new Blob([res.data], {
        type: 'text/csv;charset=utf-8',
      });

      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(blobUrl);

      setLastFilename(filename);
      toast.success('Ranking kehadiran tahunan berjaya dimuat turun.');
    } catch (err) {
      console.error(err);
      toast.error('Muat turun gagal.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleClassTimesheetDownload = async () => {
    if (!selectedClassId) {
      toast.error('Sila pilih unit.');
      return;
    }

    if (!monthStr) {
      toast.error('Sila pilih bulan.');
      return;
    }

    setIsDownloading(true);

    const params = new URLSearchParams({
      class_id: selectedClassId,
      month: monthStr,
    });

    const url = `/attendance/timesheet/download/class?${params.toString()}`;

    try {
      const res = await API.get(url, {
        headers: {
          token: localStorage.getItem('token'),
          Accept: 'text/csv,application/json',
        },
        responseType: 'blob',
        transformResponse: [(data) => data],
      });

      const contentType = (res.headers?.['content-type'] || '').toLowerCase();

      if (contentType.includes('application/json')) {
        const text = await res.data.text();
        const obj = JSON.parse(text);
        toast.error(obj?.message || 'Muat turun gagal.');
        return;
      }

      const filename = getFilenameFromDisposition(
        res.headers?.['content-disposition'],
        `timesheet_unit_${selectedClassId}_${monthStr}.csv`
      );

      const blob = new Blob([res.data], {
        type: 'text/csv;charset=utf-8',
      });

      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(blobUrl);

      setLastFilename(filename);
      toast.success('Timesheet unit berjaya dimuat turun.');
    } catch (err) {
      console.error(err);
      toast.error('Muat turun gagal.');
    } finally {
      setIsDownloading(false);
    }
  };

  const isDownloadDisabled =
    isDownloading || (filterType === 'day' ? !dateStr : !monthStr);

  return (
    <Fragment>
      <div style={pageStyle}>
        <div style={pageInnerStyle}>
          <div style={heroStyle}>
            <div style={heroGlowStyle} />

            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={heroBadgeStyle}>Pusat Laporan Jabatan</div>

              <h1 style={heroTitleStyle}>Muat Turun Laporan Kehadiran</h1>

              <p style={heroDescriptionStyle}>
                Jana fail CSV untuk laporan kehadiran harian, bulanan, timesheet
                unit, ranking tahunan dan rekod kehadiran khas dalam satu
                paparan korporat.
              </p>
            </div>
          </div>

          <ReportCard
            title="Laporan Kehadiran Rasmi"
            subtitle="Muat turun laporan kehadiran terperinci, ringkasan bulanan dan timesheet unit."
            badge="Kehadiran"
          >
            <div className="row g-3">
              <FormField label="Jenis Laporan">
                <select
                  className="form-select"
                  style={controlStyle}
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="day">Harian</option>
                  <option value="month">Bulanan</option>
                </select>
              </FormField>

              {filterType === 'day' ? (
                <FormField label="Tarikh" hint="Pilih tarikh tertentu.">
                  <input
                    type="date"
                    className="form-control"
                    style={controlStyle}
                    value={dateStr}
                    onChange={(e) => setDateStr(e.target.value)}
                  />
                </FormField>
              ) : (
                <FormField label="Bulan" hint="Pilih bulan penuh.">
                  <input
                    type="month"
                    className="form-control"
                    style={controlStyle}
                    value={monthStr}
                    onChange={(e) => setMonthStr(e.target.value)}
                  />
                </FormField>
              )}

              <FormField label="Bahagian" hint="Pilihan tambahan.">
                <select
                  className="form-select"
                  style={controlStyle}
                  value={selectedForm}
                  onChange={(e) => setSelectedForm(e.target.value)}
                >
                  <option value="">Semua Bahagian</option>
                  {uniqueForms.map((form) => (
                    <option key={form} value={form}>
                      {form}
                    </option>
                  ))}
                </select>
              </FormField>

              <FormField label="Masa Mula" hint="Tapis rekod selepas masa ini.">
                <input
                  type="time"
                  className="form-control"
                  style={controlStyle}
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                />
              </FormField>

              <FormField label="Unit" hint="Pilihan tambahan.">
                <select
                  className="form-select"
                  style={controlStyle}
                  value={selectedClassId}
                  onChange={(e) => setSelectedClassId(e.target.value)}
                >
                  <option value="">Semua Unit</option>
                  {filteredClasses.map((unit) => (
                    <option key={unit.class_id} value={unit.class_id}>
                      {displayClassLabel(unit)}
                    </option>
                  ))}
                </select>

                {incompleteClasses.length > 0 && (
                  <small className="text-warning d-block mt-1">
                    Nota: {incompleteClasses.length} unit belum lengkap
                    maklumat integrasi.
                  </small>
                )}
              </FormField>
            </div>

            <ActionRow>
              <button
                style={primaryButtonStyle}
                onClick={handleDownload}
                disabled={isDownloadDisabled}
              >
                {isDownloading ? 'Memuat Turun…' : 'Muat Turun CSV Terperinci'}
              </button>

              <button
                style={outlineBlueButtonStyle}
                onClick={handleSummaryDownload}
                disabled={isDownloading || !monthStr}
              >
                {isDownloading ? 'Memuat Turun…' : 'Muat Turun Ringkasan CSV'}
              </button>

              <button
                style={outlineDarkButtonStyle}
                onClick={handleClassTimesheetDownload}
                disabled={isDownloading || !selectedClassId || !monthStr}
              >
                {isDownloading ? 'Memuat Turun…' : 'Muat Turun Timesheet Unit'}
              </button>

              {lastFilename && (
                <span style={lastFileStyle}>
                  Fail terakhir: <strong>{lastFilename}</strong>
                </span>
              )}
            </ActionRow>

            <InfoList
              items={[
                <>
                  Pilih <strong>Harian</strong> atau <strong>Bulanan</strong>{' '}
                  sebelum muat turun.
                </>,
                <>
                  Jika unit dipilih, sistem akan menghantar parameter{' '}
                  <code>class_id</code> kepada server.
                </>,
                <>
                  Status rekod: Tidak Hadir(0), Daftar Masuk(1), Daftar
                  Keluar(2), Tiada Rekod(3).
                </>,
                <>Sebab tidak hadir hanya dipaparkan untuk status Tidak Hadir.</>,
              ]}
            />
          </ReportCard>

          <ReportCard
            title="Ringkasan Kehadiran Khas"
            subtitle="Muat turun rekod kehadiran khas seperti acara, operasi luar atau aktiviti jabatan."
            badge="Kehadiran Khas"
          >
            <div className="row g-3">
              <FormField label="Bulan" hint="Pilih bulan penuh.">
                <input
                  type="month"
                  className="form-control"
                  style={controlStyle}
                  value={monthStr}
                  onChange={(e) => setMonthStr(e.target.value)}
                />
              </FormField>

              <FormField label="Jenis Kehadiran Khas" hint="Pilihan tambahan.">
                <select
                  className="form-select"
                  style={controlStyle}
                  value={customTypeId}
                  onChange={(e) => setCustomTypeId(e.target.value)}
                >
                  <option value="">Semua Jenis Kehadiran Khas</option>
                  {customTypes.map((type) => (
                    <option key={type.type_id} value={type.type_id}>
                      {type.name || `Jenis ${type.type_id}`}
                    </option>
                  ))}
                </select>
              </FormField>

              <FormField label="Bahagian" hint="Pilihan tambahan.">
                <select
                  className="form-select"
                  style={controlStyle}
                  value={selectedForm}
                  onChange={(e) => setSelectedForm(e.target.value)}
                >
                  <option value="">Semua Bahagian</option>
                  {uniqueForms.map((form) => (
                    <option key={form} value={form}>
                      {form}
                    </option>
                  ))}
                </select>
              </FormField>

              <FormField label="Unit" hint="Pilihan tambahan.">
                <select
                  className="form-select"
                  style={controlStyle}
                  value={selectedClassId}
                  onChange={(e) => setSelectedClassId(e.target.value)}
                >
                  <option value="">Semua Unit</option>
                  {filteredClasses.map((unit) => (
                    <option key={unit.class_id} value={unit.class_id}>
                      {displayClassLabel(unit)}
                    </option>
                  ))}
                </select>
              </FormField>
            </div>

            <ActionRow>
              <button
                style={outlineBlueButtonStyle}
                onClick={handleCustomAttendanceSummaryDownload}
                disabled={isCustomDownloading || !monthStr}
              >
                {isCustomDownloading
                  ? 'Memuat Turun…'
                  : 'Muat Turun Ringkasan Kehadiran Khas'}
              </button>
            </ActionRow>

            <InfoList
              items={[
                <>Muat turun rekod kehadiran khas mengikut bulan.</>,
                <>
                  Pilih satu jenis kehadiran khas, atau biarkan sebagai semua
                  jenis.
                </>,
                <>
                  Output akan menandakan tarikh dengan <code>X</code> apabila
                  rekod kehadiran khas wujud.
                </>,
              ]}
            />
          </ReportCard>

          <ReportCard
            title="Laporan Prestasi Kakitangan"
            subtitle="Muat turun rekod prestasi atau pencapaian kakitangan berdasarkan tempoh pilihan."
            badge="Prestasi"
          >
            <div className="row g-3">
              <FormField label="Skop">
                <select
                  className="form-select"
                  style={controlStyle}
                  value={amalanScope}
                  onChange={(e) => setAmalanScope(e.target.value)}
                >
                  <option value="month">Bulanan</option>
                  <option value="all">Sepanjang Masa</option>
                </select>
              </FormField>

              {amalanScope === 'month' && (
                <FormField label="Bulan">
                  <input
                    type="month"
                    className="form-control"
                    style={controlStyle}
                    value={amalanMonth}
                    onChange={(e) => setAmalanMonth(e.target.value)}
                  />
                </FormField>
              )}

              <FormField
                label="Had Rekod"
                hint="Pilih semua untuk masukkan semua kakitangan."
              >
                <select
                  className="form-select"
                  style={controlStyle}
                  value={amalanLimit}
                  onChange={(e) => setAmalanLimit(e.target.value)}
                >
                  <option value="50">Top 50 kakitangan</option>
                  <option value="100">Top 100 kakitangan</option>
                  <option value="200">Top 200 kakitangan</option>
                  <option value="all">Semua kakitangan</option>
                </select>
              </FormField>
            </div>

            <ActionRow>
              <button style={primaryButtonStyle} onClick={handleAmalanBaikDownload}>
                Muat Turun CSV
              </button>
            </ActionRow>

            <InfoList
              items={[
                <>
                  Muat turun laporan prestasi kakitangan mengikut bulan atau
                  sepanjang masa.
                </>,
                <>
                  Fail boleh digunakan untuk semakan dalaman, laporan
                  pengurusan atau audit operasi.
                </>,
                <>
                  Gunakan <code>limit_top</code> untuk menetapkan jumlah rekod
                  yang dimasukkan.
                </>,
              ]}
            />
          </ReportCard>

          <ReportCard
            title="Ranking Kehadiran Tahunan"
            subtitle="Jana ranking kehadiran penuh bagi tahun semasa untuk pemantauan prestasi jabatan."
            badge="Tahunan"
          >
            <div className="row g-3 align-items-end">
              <FormField label="Tahun" hint="Ranking penuh Januari hingga Disember.">
                <input
                  type="number"
                  className="form-control"
                  style={controlStyle}
                  value={rankingYear}
                  onChange={(e) => setRankingYear(e.target.value)}
                />
              </FormField>
            </div>

            <ActionRow>
              <button
                style={outlineBlueButtonStyle}
                onClick={handleYearlyRankingDownload}
                disabled={isDownloading || !rankingYear}
              >
                {isDownloading ? 'Memuat Turun…' : 'Muat Turun Ranking Tahunan'}
              </button>
            </ActionRow>

            <InfoList
              items={[
                <>Menjana ranking kehadiran untuk satu tahun penuh.</>,
                <>Kakitangan disusun mengikut peratus kehadiran tertinggi.</>,
                <>
                  CSV mengandungi: Ranking, Nama Kakitangan, Unit, Hari Hadir,
                  Jumlah Hari dan Peratus Kehadiran.
                </>,
                <>
                  Format nama fail: <code>ranking_kehadiran_YYYY.csv</code>.
                </>,
              ]}
            />
          </ReportCard>
        </div>
      </div>
    </Fragment>
  );
};

export default DownloadAttendancePage;

const ReportCard = ({ title, subtitle, badge, children }) => {
  return (
    <div className="card rounded-25 mt-4" style={cardStyle}>
      <div style={cardHeaderStyle}>
        <div>
          <div style={cardKickerStyle}>{badge}</div>
          <h5 style={cardTitleStyle}>{title}</h5>
          {subtitle && <p style={cardSubtitleStyle}>{subtitle}</p>}
        </div>
      </div>

      <div className="card-body" style={cardBodyStyle}>
        {children}
      </div>
    </div>
  );
};

const FormField = ({ label, hint, children }) => {
  return (
    <div className="col-md-3">
      <label className="form-label" style={labelStyle}>
        {label}
      </label>
      {children}
      {hint && <small style={hintStyle}>{hint}</small>}
    </div>
  );
};

const ActionRow = ({ children }) => {
  return (
    <div
      className="mt-4 d-flex align-items-center gap-2"
      style={{ flexWrap: 'wrap' }}
    >
      {children}
    </div>
  );
};

const InfoList = ({ items }) => {
  return (
    <>
      <hr className="my-4" />
      <ul className="small mb-0" style={infoListStyle}>
        {items.map((item, index) => (
          <li key={index}>{item}</li>
        ))}
      </ul>
    </>
  );
};

const pageStyle = {
  minHeight: '100vh',
  background: 'linear-gradient(180deg, #f8fbff 0%, #eef6ff 100%)',
  padding: '28px 24px 44px',
};

const pageInnerStyle = {
  maxWidth: 1180,
  margin: '0 auto',
};

const heroStyle = {
  background: 'linear-gradient(135deg, #0B4C9C 0%, #0F65C9 55%, #1E88E5 100%)',
  borderRadius: 28,
  padding: '34px 36px',
  marginBottom: 8,
  color: '#ffffff',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 24px 70px rgba(11, 76, 156, 0.22)',
};

const heroGlowStyle = {
  position: 'absolute',
  right: -80,
  top: -80,
  width: 260,
  height: 260,
  borderRadius: '50%',
  background: 'rgba(255,255,255,0.11)',
};

const heroBadgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  background: 'rgba(255,255,255,0.14)',
  border: '1px solid rgba(255,255,255,0.22)',
  borderRadius: 999,
  padding: '8px 13px',
  fontSize: 12,
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  marginBottom: 14,
};

const heroTitleStyle = {
  fontSize: 'clamp(30px, 4vw, 48px)',
  fontWeight: 950,
  letterSpacing: -1.4,
  margin: 0,
};

const heroDescriptionStyle = {
  maxWidth: 780,
  margin: '10px 0 0',
  color: 'rgba(255,255,255,0.82)',
  fontSize: 15,
  lineHeight: 1.7,
};

const cardStyle = {
  border: '1px solid #dbeafe',
  borderRadius: 22,
  boxShadow: '0 16px 40px rgba(11, 76, 156, 0.08)',
  overflow: 'hidden',
  background: '#ffffff',
};

const cardHeaderStyle = {
  background: '#ffffff',
  borderBottom: '1px solid #dbeafe',
  padding: '20px 24px',
};

const cardKickerStyle = {
  display: 'inline-flex',
  background: '#eff6ff',
  border: '1px solid #bfdbfe',
  color: '#0B4C9C',
  borderRadius: 999,
  padding: '6px 11px',
  fontSize: 12,
  fontWeight: 900,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: 8,
};

const cardTitleStyle = {
  color: '#0f172a',
  margin: 0,
  fontWeight: 900,
};

const cardSubtitleStyle = {
  margin: '6px 0 0',
  color: '#64748b',
  fontSize: 14,
  lineHeight: 1.6,
};

const cardBodyStyle = {
  backgroundColor: '#ffffff',
  padding: 24,
};

const labelStyle = {
  color: '#334155',
  fontWeight: 800,
  marginBottom: 6,
};

const controlStyle = {
  borderRadius: 14,
  minHeight: 46,
  borderColor: '#bfdbfe',
  boxShadow: 'none',
};

const hintStyle = {
  color: '#64748b',
  display: 'block',
  marginTop: 6,
};

const primaryButtonStyle = {
  backgroundColor: '#0B4C9C',
  color: '#ffffff',
  border: 'none',
  borderRadius: 14,
  fontWeight: 800,
  padding: '11px 18px',
  minHeight: 44,
  opacity: 1,
};

const outlineBlueButtonStyle = {
  backgroundColor: '#ffffff',
  color: '#0B4C9C',
  border: '1px solid #0B4C9C',
  borderRadius: 14,
  fontWeight: 800,
  padding: '11px 18px',
  minHeight: 44,
};

const outlineDarkButtonStyle = {
  backgroundColor: '#ffffff',
  color: '#0f172a',
  border: '1px solid #cbd5e1',
  borderRadius: 14,
  fontWeight: 800,
  padding: '11px 18px',
  minHeight: 44,
};

const lastFileStyle = {
  color: '#64748b',
  fontSize: 13,
  marginLeft: 4,
};

const infoListStyle = {
  color: '#64748b',
  lineHeight: 1.8,
};